import type { AgriMaterialUsageRow } from '@/lib/api/agri-material-usage-api'
import type { GrowerPaymentPeriod } from '@/lib/api/grower-payment-api'
import { ymdVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

// ============================================================================
// Báo cáo của hộ trồng chè: kỳ Tuần / Tháng / Năm và dữ liệu biểu đồ.
//
// Mốc kỳ phải trùng đúng `period_start` server trả (date_trunc của Postgres:
// tuần bắt đầu Thứ Hai). Mọi phép tính ngày làm trên Date.UTC của chuỗi
// YYYY-MM-DD — không đọc giờ máy, vì "hôm nay" đã được chốt theo lịch Việt Nam.
// ============================================================================

export const GROWER_REPORT_PERIODS = ['week', 'month', 'year'] as const
export type GrowerReportPeriod = (typeof GROWER_REPORT_PERIODS)[number]
export const DEFAULT_GROWER_REPORT_PERIOD: GrowerReportPeriod = 'month'

/** Số cột của mỗi kỳ: 12 tuần, 12 tháng, 5 năm gần nhất. */
const PERIOD_COUNT: Record<GrowerReportPeriod, number> = {
    week: 12,
    month: 12,
    year: 5,
}

/** Số chuỗi tối đa trên một biểu đồ — đúng số màu `--chart-1..8` đã kiểm
 *  định. Nhiều hơn thì gộp phần nhỏ nhất vào "Khác". */
export const GROWER_REPORT_SERIES_CAP = 8

export function parseGrowerReportPeriod(
    raw: unknown,
): GrowerReportPeriod | undefined {
    return GROWER_REPORT_PERIODS.includes(raw as GrowerReportPeriod)
        ? (raw as GrowerReportPeriod)
        : undefined
}

/** Ngày hôm nay theo lịch Việt Nam (YYYY-MM-DD). */
export function growerReportToday(now: Date = new Date()): string {
    return ymdVN(now.toISOString())
}

function parseYmd(ymd: string): Date {
    const [y, mo, d] = ymd.split('-').map(Number)
    return new Date(Date.UTC(y, mo - 1, d))
}

function toYmd(d: Date): string {
    return d.toISOString().slice(0, 10)
}

function addDays(d: Date, days: number): Date {
    return new Date(
        Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days),
    )
}

function periodStartOf(period: GrowerReportPeriod, d: Date): Date {
    if (period === 'week') {
        // getUTCDay: 0 = Chủ nhật. Lùi về Thứ Hai cùng tuần.
        return addDays(d, -((d.getUTCDay() + 6) % 7))
    }
    if (period === 'month') {
        return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
    }
    return new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
}

/** Đầu kỳ thứ `offset` tính từ kỳ chứa `start` (âm = lùi). */
function shiftPeriod(
    period: GrowerReportPeriod,
    start: Date,
    offset: number,
): Date {
    if (period === 'week') return addDays(start, offset * 7)
    if (period === 'month') {
        return new Date(
            Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + offset, 1),
        )
    }
    return new Date(Date.UTC(start.getUTCFullYear() + offset, 0, 1))
}

/** Ngày cuối của kỳ bắt đầu ở `start`. */
function periodEndOf(period: GrowerReportPeriod, start: Date): Date {
    return addDays(shiftPeriod(period, start, 1), -1)
}

export interface GrowerReportWindow {
    /** Ngày đầu kỳ cũ nhất, gửi API làm `from` / `from_date`. */
    from: string
    /** Ngày cuối kỳ hiện tại (trọn kỳ), gửi API làm `to` / `to_date`. */
    to: string
    /** Đầu từng kỳ, cũ → mới — trục X của biểu đồ. */
    starts: string[]
}

export function growerReportWindow(
    period: GrowerReportPeriod,
    today: string,
): GrowerReportWindow {
    const current = periodStartOf(period, parseYmd(today))
    const count = PERIOD_COUNT[period]
    const starts = Array.from({ length: count }, (_, i) =>
        toYmd(shiftPeriod(period, current, i - (count - 1))),
    )
    return {
        from: starts[0],
        to: toYmd(periodEndOf(period, current)),
        starts,
    }
}

function dmy(d: Date): string {
    const dd = String(d.getUTCDate()).padStart(2, '0')
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
    return `${dd}/${mm}/${d.getUTCFullYear()}`
}

/** Nhãn ngắn trên trục X: tuần `dd/MM` (Thứ Hai), tháng `MM/yyyy`, năm `yyyy`. */
export function periodAxisLabel(
    period: GrowerReportPeriod,
    start: string,
): string {
    const [y, mo, d] = start.split('-')
    if (period === 'week') return `${d}/${mo}`
    if (period === 'month') return `${mo}/${y}`
    return y
}

/** Khoảng ngày đầy đủ của kỳ `dd/MM/yyyy – dd/MM/yyyy` (tooltip, bảng). */
export function periodRangeLabel(
    period: GrowerReportPeriod,
    start: string,
): string {
    const s = parseYmd(start)
    return `${dmy(s)} – ${dmy(periodEndOf(period, s))}`
}

function dm(d: Date): string {
    return dmy(d).slice(0, 5)
}

/**
 * Tên kỳ bằng lời cho người không quen đọc biểu đồ: kỳ hiện tại / kỳ trước gọi
 * tên ("Tuần này", "Tháng trước", "Năm ngoái"), kỳ cũ hơn ghi khoảng ngày.
 * `starts` là các kỳ đang xem, cũ → mới (kỳ cuối = kỳ hiện tại).
 */
export function periodHumanLabel(
    period: GrowerReportPeriod,
    start: string,
    starts: string[],
): string {
    const offset = starts.length - 1 - starts.indexOf(start)
    const s = parseYmd(start)
    if (period === 'week') {
        const range = `${dm(s)} – ${dm(periodEndOf(period, s))}`
        if (offset === 0) return m.my_grower_report_this_week({ range })
        if (offset === 1) return m.my_grower_report_last_week({ range })
        return m.my_grower_report_week({ range })
    }
    if (period === 'month') {
        const month = periodAxisLabel(period, start)
        if (offset === 0) return m.my_grower_report_this_month({ month })
        if (offset === 1) return m.my_grower_report_last_month({ month })
        return m.my_grower_report_month({ month })
    }
    const year = periodAxisLabel(period, start)
    if (offset === 0) return m.my_grower_report_this_year({ year })
    if (offset === 1) return m.my_grower_report_last_year({ year })
    return m.my_grower_report_year({ year })
}

/** `YYYY-MM-DD` → `dd/MM/yyyy`. */
export function formatReportDate(ymd: string): string {
    return dmy(parseYmd(ymd))
}

// ----------------------------------------------------------------------------
// Vật tư đã dùng
// ----------------------------------------------------------------------------

export type UsageCategory = 'fertilizer' | 'pesticide'

export interface UsageSeries {
    /** dataKey của recharts. */
    key: string
    /** Rỗng = chuỗi "Khác" gộp các vật tư nhỏ. */
    materialId: string
    name: string
    total: number
    color: string
}

export interface UsageMaterialTotal {
    materialId: string
    name: string
    total: number
    /** Lượng theo từng kỳ (chỉ kỳ có dùng) — cho danh sách bằng lời. */
    byPeriod: Record<string, number>
}

export interface UsageChart {
    /** Mã đơn vị (kg|bag|bottle|pack) — một biểu đồ chỉ một đơn vị. */
    unit: string
    rows: Array<{ period: string } & Record<string, number | string>>
    series: UsageSeries[]
    /** Tổng từng vật tư trong cả khoảng, lớn → nhỏ — bảng dưới biểu đồ. */
    materials: UsageMaterialTotal[]
}

const OTHER_KEY = 'other'

/**
 * Dữ liệu biểu đồ vật tư của một nhóm (phân bón / thuốc), mỗi đơn vị một
 * biểu đồ: cộng kg với bao là vô nghĩa, và chung một trục y thì cột "2 bao"
 * trông nhỏ hơn "50 kg". Trong một biểu đồ mỗi vật tư là một chuỗi; các bãi của
 * cùng vật tư trong cùng kỳ được cộng lại (lọc bãi đã làm ở server).
 */
export function buildUsageCharts(
    rows: AgriMaterialUsageRow[],
    category: UsageCategory,
    starts: string[],
): UsageChart[] {
    const inWindow = new Set(starts)
    // unit → materialId → period → qty
    const byUnit = new Map<
        string,
        Map<string, { name: string; byPeriod: Map<string, number> }>
    >()
    for (const r of rows) {
        if (r.material_category !== category) continue
        if (!r.period_start || !inWindow.has(r.period_start)) continue
        let materials = byUnit.get(r.unit)
        if (!materials) {
            materials = new Map()
            byUnit.set(r.unit, materials)
        }
        let mat = materials.get(r.material_id)
        if (!mat) {
            mat = { name: r.material_name, byPeriod: new Map() }
            materials.set(r.material_id, mat)
        }
        mat.byPeriod.set(
            r.period_start,
            (mat.byPeriod.get(r.period_start) ?? 0) + r.quantity,
        )
    }

    const charts: UsageChart[] = []
    for (const [unit, materials] of byUnit) {
        const totals: (Omit<UsageMaterialTotal, 'byPeriod'> & {
            byPeriod: Map<string, number>
        })[] = []
        for (const [materialId, mat] of materials) {
            let total = 0
            for (const q of mat.byPeriod.values()) total += q
            totals.push({
                materialId,
                name: mat.name,
                total,
                byPeriod: mat.byPeriod,
            })
        }
        totals.sort(
            (a, b) => b.total - a.total || a.name.localeCompare(b.name, 'vi'),
        )

        const overflow = totals.length > GROWER_REPORT_SERIES_CAP
        const shown = overflow
            ? totals.slice(0, GROWER_REPORT_SERIES_CAP - 1)
            : totals
        const folded = overflow
            ? totals.slice(GROWER_REPORT_SERIES_CAP - 1)
            : []
        // Màu theo thứ tự tên của vật tư, không theo thứ hạng lượng dùng.
        const named = [...shown].sort((a, b) =>
            a.name.localeCompare(b.name, 'vi'),
        )

        const series: UsageSeries[] = named.map((t, i) => ({
            key: `m_${t.materialId}`,
            materialId: t.materialId,
            name: t.name,
            total: t.total,
            color: `var(--chart-${i + 1})`,
        }))
        if (folded.length > 0) {
            series.push({
                key: OTHER_KEY,
                materialId: '',
                name: m.my_grower_report_other(),
                total: folded.reduce((s, t) => s + t.total, 0),
                color: `var(--chart-${GROWER_REPORT_SERIES_CAP})`,
            })
        }

        const chartRows = starts.map((period) => {
            const out: { period: string } & Record<string, number | string> = {
                period,
            }
            for (const t of named)
                out[`m_${t.materialId}`] = t.byPeriod.get(period) ?? 0
            if (folded.length > 0) {
                out[OTHER_KEY] = folded.reduce(
                    (s, t) => s + (t.byPeriod.get(period) ?? 0),
                    0,
                )
            }
            return out
        })

        charts.push({
            unit,
            rows: chartRows,
            series,
            materials: totals.map(({ materialId, name, total, byPeriod }) => ({
                materialId,
                name,
                total,
                byPeriod: Object.fromEntries(byPeriod),
            })),
        })
    }
    return charts
}

export interface UsageLineItem {
    materialId: string
    name: string
    unit: string
    qty: number
}

/**
 * Danh sách bằng lời "kỳ nào dùng bao nhiêu" (gộp mọi đơn vị, mỗi vật tư giữ
 * đơn vị của nó), kỳ mới nhất lên đầu, bỏ kỳ không dùng gì.
 */
export function buildUsagePeriodLines(
    charts: UsageChart[],
    starts: string[],
): Array<{ period: string; items: UsageLineItem[] }> {
    const out: Array<{ period: string; items: UsageLineItem[] }> = []
    for (const period of [...starts].reverse()) {
        const items: UsageLineItem[] = []
        for (const chart of charts) {
            for (const mat of chart.materials) {
                const qty = mat.byPeriod[period] ?? 0
                if (qty > 0) {
                    items.push({
                        materialId: mat.materialId,
                        name: mat.name,
                        unit: chart.unit,
                        qty,
                    })
                }
            }
        }
        if (items.length === 0) continue
        items.sort((a, b) => a.name.localeCompare(b.name, 'vi'))
        out.push({ period, items })
    }
    return out
}

// ----------------------------------------------------------------------------
// Tiền chè đã nhận
// ----------------------------------------------------------------------------

const compactVnd = new Intl.NumberFormat('vi-VN', {
    notation: 'compact',
    maximumFractionDigits: 1,
})

/** Số tiền rút gọn cho trục Y hẹp trên điện thoại (vd 1.500.000 → "1,5 Tr"). */
export function formatCompactAmount(n: number): string {
    return compactVnd.format(n)
}

export function buildMoneyChart(
    periods: GrowerPaymentPeriod[] | undefined,
    starts: string[],
): Array<{ period: string; amount: number }> {
    const byStart = new Map(
        (periods ?? []).map((p) => [p.period_start, p.total_amount]),
    )
    return starts.map((period) => ({
        period,
        amount: byStart.get(period) ?? 0,
    }))
}

/** Kỳ có nhận tiền, mới nhất lên đầu — danh sách bằng lời dưới số tổng. */
export function buildMoneyPeriodLines(
    rows: Array<{ period: string; amount: number }>,
): Array<{ period: string; amount: number }> {
    return [...rows].reverse().filter((r) => r.amount > 0)
}
